import React, { useState, useEffect } from 'react';
import {
    Building, Printer, CreditCard, User, Save, Moon, Sun, CheckCircle2,
    ImagePlus, X, Sparkles, ExternalLink, Eye, EyeOff, Loader2, Plug,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { cn } from '../../utils/theme';
import { useBusinessConfig } from '../../hooks/useBusinessConfig';
import { api } from '../../utils/api';
import { getAISettings, saveAISettings, testAIConnection } from '../../services/AIService';
import { getPrinterConfig, savePrinterConfig } from '../../hooks/usePrinterConfig';

// ⚠️ Definido FUERA del componente: si se define dentro, React lo recrea en cada
// render y el input pierde el foco al escribir (no se puede teclear normal).
function Field({ label, id, value, onChange, placeholder = '', multiline = false }: {
    label: string; id: string; value: string; onChange: (v: string) => void;
    placeholder?: string; multiline?: boolean;
}) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label}
            </label>
            {multiline ? (
                <textarea
                    id={id}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                />
            ) : (
                <input
                    id={id}
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
            )}
        </div>
    );
}

export default function Settings() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('general');
    const [saved, setSaved] = useState(false);

    const [darkMode, setDarkMode] = useState(() =>
        localStorage.getItem('theme') === 'dark' ||
        document.documentElement.classList.contains('dark')
    );
    const [paperWidth, setPaperWidth] = useState<number>(() => getPrinterConfig().paperWidth);
    const [printerIp, setPrinterIp] = useState<string>(() => localStorage.getItem('printer_ip') || '');
    const [printerPort, setPrinterPort] = useState<string>(() => localStorage.getItem('printer_port') || '9100');

    const { config, save } = useBusinessConfig();
    const [form, setForm] = useState(config);

    const set = (key: keyof typeof form, value: string) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const toggleTheme = () => {
        const isDark = !darkMode;
        setDarkMode(isDark);
        document.documentElement.classList.toggle('dark', isDark);
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
    };

    const handleSaveGeneral = () => {
        save(form);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
    };

    // Logo upload → data URL
    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => set('logoUrl', ev.target?.result as string);
        reader.readAsDataURL(file);
    };

    // ── AI providers ─────────────────────────────────────────────────────────
    const qc = useQueryClient();
    const { data: aiConfig } = useQuery({
        queryKey: ['ai-settings'],
        queryFn: getAISettings,
    });

    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [modelInput, setModelInput] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

    // Sincronizar selección con el proveedor activo al cargar
    useEffect(() => {
        if (aiConfig && !selectedProvider) {
            setSelectedProvider(aiConfig.active);
            const active = aiConfig.providers.find(p => p.id === aiConfig.active);
            setModelInput(active?.model ?? '');
        }
    }, [aiConfig, selectedProvider]);

    const selectedInfo = aiConfig?.providers.find(p => p.id === selectedProvider);

    const saveAiMutation = useMutation({
        mutationFn: () => saveAISettings({
            provider: selectedProvider,
            api_key: apiKeyInput || undefined,
            model: modelInput || undefined,
        }),
        onSuccess: () => {
            toast.success('Configuración de IA guardada');
            setApiKeyInput('');
            setTestResult(null);
            qc.invalidateQueries({ queryKey: ['ai-settings'] });
        },
        onError: () => toast.error('No se pudo guardar la configuración'),
    });

    const testAiMutation = useMutation({
        mutationFn: testAIConnection,
        onSuccess: (r) => setTestResult({ ok: r.status === 'connected', msg: r.status === 'connected' ? `Conectado a ${r.provider}` : (r.message ?? 'Error') }),
        onError: (e: any) => setTestResult({ ok: false, msg: e.response?.data?.message ?? 'No se pudo conectar' }),
    });

    const tabs = [
        { id: 'general', label: 'General', icon: Building },
        { id: 'hardware', label: 'Hardware', icon: Printer },
        { id: 'payments', label: 'Pagos', icon: CreditCard },
        { id: 'ai', label: 'IA', icon: Sparkles },
        { id: 'account', label: 'Cuenta', icon: User },
    ];


    return (
        <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuración</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Administra las preferencias de tu sistema</p>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
                {/* Sidebar */}
                <div className="w-full md:w-56 flex flex-col gap-1">
                    {tabs.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={cn(
                                'flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors font-medium text-sm',
                                activeTab === id
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-neutral-800'
                            )}
                        >
                            <Icon size={18} /> {label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-100 dark:border-neutral-700 p-6 min-h-[500px]">

                    {/* ── GENERAL ── */}
                    {activeTab === 'general' && (
                        <div className="space-y-6 max-w-lg">
                            <h2 className="text-lg font-semibold">Información del Negocio</h2>

                            {/* Logo */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Logo del negocio
                                </label>
                                <div className="flex items-center gap-4">
                                    {form.logoUrl ? (
                                        <div className="relative">
                                            <img
                                                src={form.logoUrl}
                                                alt="Logo"
                                                className="h-16 w-auto max-w-[120px] object-contain rounded-lg border border-gray-200 dark:border-neutral-600 bg-white p-1"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => set('logoUrl', '')}
                                                className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                                                aria-label="Quitar logo"
                                            >
                                                <X size={10} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex h-16 w-24 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-700">
                                            <ImagePlus size={20} className="text-gray-400" />
                                        </div>
                                    )}
                                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-neutral-600 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition">
                                        <ImagePlus size={14} />
                                        {form.logoUrl ? 'Cambiar logo' : 'Subir logo'}
                                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="sr-only" />
                                    </label>
                                </div>
                                <p className="mt-1 text-xs text-gray-400">Aparecerá en la cabecera del ticket impreso.</p>
                            </div>

                            <div className="grid gap-4">
                                <Field id="name" label="Nombre del negocio" value={form.name} onChange={v => set('name', v)} placeholder="Mi Restaurante" />
                                <Field id="slogan" label="Eslogan (opcional)" value={form.slogan} onChange={v => set('slogan', v)} placeholder="El mejor sabor de la ciudad" />
                                <Field id="address" label="Dirección" value={form.address} onChange={v => set('address', v)} placeholder="Calle 123 # 45-67, Ciudad" />
                                <Field id="phone" label="Teléfono" value={form.phone} onChange={v => set('phone', v)} placeholder="(601) 123 4567" />
                                <Field id="nit" label="NIT / Identificación fiscal" value={form.nit} onChange={v => set('nit', v)} placeholder="900.123.456-7" />
                                <Field id="receiptFooter" label="Pie del ticket" value={form.receiptFooter} onChange={v => set('receiptFooter', v)} placeholder="¡Gracias por su visita!" multiline />
                            </div>

                            {/* Theme toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {darkMode ? <Moon size={18} className="text-purple-400" /> : <Sun size={18} className="text-amber-500" />}
                                    <div>
                                        <p className="text-sm font-medium">Modo Oscuro</p>
                                        <p className="text-xs text-gray-500">Cambia la apariencia</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" checked={darkMode} onChange={toggleTheme} className="sr-only peer" />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                                </label>
                            </div>

                            <div className="pt-2 border-t border-gray-100 dark:border-neutral-700">
                                <button
                                    onClick={handleSaveGeneral}
                                    className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition"
                                >
                                    {saved ? <CheckCircle2 size={16} /> : <Save size={16} />}
                                    {saved ? '¡Guardado!' : 'Guardar cambios'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── HARDWARE ── */}
                    {activeTab === 'hardware' && (
                        <div className="space-y-6 max-w-lg">
                            <h2 className="text-lg font-semibold">Hardware e Impresión</h2>
                            <div className="p-4 border border-blue-100 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 rounded-lg text-blue-800 dark:text-blue-300 text-sm flex gap-3">
                                <Printer size={20} className="shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-semibold">Impresoras Térmicas</p>
                                    <p className="mt-1">El sistema usa la impresión nativa del navegador. Elige el ancho de tu rollo para que el ticket salga perfecto.</p>
                                </div>
                            </div>

                            {/* Ancho del papel */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Ancho del papel (impresora térmica)
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { w: 58, label: '58 mm', desc: 'XP-58 y similares' },
                                        { w: 80, label: '80 mm', desc: 'POS estándar' },
                                    ].map(opt => (
                                        <button
                                            key={opt.w}
                                            type="button"
                                            onClick={() => setPaperWidth(opt.w)}
                                            className={cn(
                                                'rounded-xl border px-3 py-3 text-center transition',
                                                paperWidth === opt.w
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                                                    : 'border-gray-200 dark:border-neutral-600 hover:border-blue-300',
                                            )}
                                        >
                                            <p className="font-bold text-sm">{opt.label}</p>
                                            <p className="text-[11px] text-gray-500">{opt.desc}</p>
                                        </button>
                                    ))}
                                    <div className="rounded-xl border border-gray-200 dark:border-neutral-600 px-3 py-2 text-center">
                                        <p className="text-[11px] text-gray-500 mb-1">Otro (mm)</p>
                                        <input
                                            type="number"
                                            min={40}
                                            max={120}
                                            value={paperWidth}
                                            onChange={e => setPaperWidth(Math.max(40, Math.min(120, Number(e.target.value) || 80)))}
                                            className="w-full rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-2 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { savePrinterConfig({ paperWidth }); toast.success(`Ancho de papel: ${paperWidth}mm guardado`); }}
                                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                >
                                    <Save size={15} /> Guardar ancho
                                </button>
                                <p className="text-xs text-gray-500 mt-2">
                                    Tip: en el diálogo de impresión del navegador, elige tu impresora térmica y desactiva
                                    "encabezados y pies de página" y los márgenes para mejor resultado.
                                </p>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        IP de la impresora de cocina (YJ-8360 / TCP 9100)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="192.168.1.100"
                                        value={printerIp}
                                        onChange={e => setPrinterIp(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Puerto de la impresora
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="65535"
                                        value={printerPort}
                                        onChange={e => setPrinterPort(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        localStorage.setItem('printer_ip', printerIp);
                                        localStorage.setItem('printer_port', printerPort);
                                        toast.success('Configuración de impresora guardada');
                                    }}
                                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                >
                                    <Save size={15} /> Guardar impresora
                                </button>
                                <p className="text-xs text-gray-500">
                                    Si llena la IP y el puerto, el sistema intentará imprimir la comanda de cocina automáticamente al enviarla a cocina.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── PAYMENTS ── */}
                    {activeTab === 'payments' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Métodos de Pago</h2>
                            <p className="text-sm text-gray-500">Gestiona los métodos de pago aceptados.</p>
                            <button
                                onClick={() => navigate('/cash/methods')}
                                className="p-4 border rounded-xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition text-left group w-full max-w-sm"
                            >
                                <div className="flex items-center gap-3 mb-1">
                                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                                        <CreditCard size={20} />
                                    </div>
                                    <span className="font-semibold text-sm">Configurar Métodos</span>
                                </div>
                                <p className="text-xs text-gray-500">Agregar o quitar métodos (Efectivo, Tarjeta, Transferencia).</p>
                            </button>
                        </div>
                    )}

                    {/* ── IA ── */}
                    {activeTab === 'ai' && (
                        <div className="space-y-6 max-w-lg">
                            <div>
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <Sparkles size={18} className="text-violet-500" /> Inteligencia Artificial
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Elige el proveedor de IA y configura tu API key. Los cambios se aplican al instante, sin reiniciar nada.
                                </p>
                            </div>

                            {/* Proveedor activo */}
                            {aiConfig && (
                                <div className="flex items-center gap-2 rounded-lg bg-violet-50 border border-violet-200 px-4 py-3 text-sm">
                                    <Sparkles size={14} className="text-violet-500 shrink-0" />
                                    <span className="text-violet-700">
                                        Proveedor activo: <strong>
                                            {aiConfig.providers.find(p => p.id === aiConfig.active)?.name ?? aiConfig.active}
                                        </strong>
                                    </span>
                                </div>
                            )}

                            {/* Selector de proveedor */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Proveedor</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {(aiConfig?.providers ?? []).map(p => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => { setSelectedProvider(p.id); setModelInput(p.model); setApiKeyInput(''); setTestResult(null); }}
                                            className={cn(
                                                'rounded-xl border px-3 py-3 text-center text-sm font-medium transition relative',
                                                selectedProvider === p.id
                                                    ? 'border-violet-400 bg-violet-50 text-violet-700'
                                                    : 'border-gray-200 bg-white text-gray-600 hover:border-violet-200',
                                            )}
                                        >
                                            {p.name.split(' ')[0]}
                                            {p.configured && (
                                                <CheckCircle2 size={13} className="absolute top-1.5 right-1.5 text-emerald-500" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {selectedInfo && (
                                <>
                                    {/* API Key */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            API Key de {selectedInfo.name}
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showKey ? 'text' : 'password'}
                                                value={apiKeyInput}
                                                onChange={e => setApiKeyInput(e.target.value)}
                                                placeholder={selectedInfo.configured ? `Configurada: ${selectedInfo.keyMasked} (deja vacío para mantener)` : 'Pega tu API key aquí'}
                                                className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                                            />
                                            <button type="button" onClick={() => setShowKey(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                                {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                                            </button>
                                        </div>
                                        <a href={selectedInfo.keyUrl} target="_blank" rel="noopener noreferrer"
                                           className="inline-flex items-center gap-1 text-violet-600 hover:underline text-xs font-medium mt-1.5">
                                            Obtener API Key <ExternalLink size={10} />
                                        </a>
                                    </div>

                                    {/* Modelo */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Modelo <span className="text-gray-400 font-normal">(opcional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={modelInput}
                                            onChange={e => setModelInput(e.target.value)}
                                            placeholder={selectedInfo.model}
                                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                                        />
                                    </div>

                                    {/* Resultado del test */}
                                    {testResult && (
                                        <div className={cn(
                                            'rounded-lg px-4 py-2.5 text-sm border',
                                            testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
                                        )}>
                                            {testResult.ok ? '✓ ' : '✕ '}{testResult.msg}
                                        </div>
                                    )}

                                    {/* Acciones */}
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => saveAiMutation.mutate()}
                                            disabled={saveAiMutation.isPending}
                                            className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                                        >
                                            {saveAiMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                                            Guardar y activar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => testAiMutation.mutate()}
                                            disabled={testAiMutation.isPending}
                                            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                                        >
                                            {testAiMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plug size={15} />}
                                            Probar conexión
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ── ACCOUNT ── */}
                    {activeTab === 'account' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Mi Cuenta</h2>
                            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-xl">
                                <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                                    <User size={28} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-sm">Perfil de Usuario</h3>
                                    <p className="text-xs text-gray-500">Gestiona tu información personal y contraseña.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/profile')}
                                    className="px-4 py-2 text-sm bg-white border border-gray-200 shadow-sm rounded-lg hover:bg-gray-50 dark:bg-neutral-700 dark:border-neutral-600"
                                >
                                    Ir al Perfil
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
